import { useEffect, useMemo } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap"
import ThemeModal from "components/Common/ThemeModal"
import {
  resolveCustomerTypeKey,
  formatCustomerTypeLabel,
  getCustomerTypeFieldMeta,
} from "helpers/customerOnboarding"

const CUSTOMER_TYPES = ["general", "service", "banker"]

const getCustomerInitialValues = preOrder => {
  const info = preOrder?.pdf_content?.customer_info ?? {}
  return {
    name: info.name ?? "",
    mobile: info.mobile ?? "",
    email: info.email ?? "",
    customer_type: resolveCustomerTypeKey(info.customer_type) || "general",
    designation: info.designation ?? "",
    company_name: info.company_name ?? "",
    bank_name: info.bank_name ?? "",
    branch_name: info.branch_name ?? "",
    address: info.address ?? "",
  }
}

const getVehicleInitialValues = preOrder => {
  const info = preOrder?.pdf_content?.vehicle_info ?? {}
  return {
    name: info.name ?? "",
    stock_id: info.stock_id ?? "",
    full_chassis: info.full_chassis ?? "",
    full_engine: info.full_engine ?? "",
    year_model: info.year_model ?? "",
    color: info.color ?? "",
    cc: info.cc ?? "",
    fuel: info.fuel ?? "",
    brta_reg: info.brta_reg ?? "",
    vehicle_description: preOrder?.vehicle_description ?? "",
  }
}

const getNotesInitialValues = preOrder => ({
  notes: preOrder?.notes ?? "",
})

const customerValidation = Yup.object({
  name: Yup.string().nullable().max(255, "Name is too long"),
  mobile: Yup.string().nullable().max(20, "Mobile is too long"),
  email: Yup.string().nullable().email("Invalid email").max(255, "Email is too long"),
  customer_type: Yup.string().oneOf(CUSTOMER_TYPES, "Invalid customer type"),
  designation: Yup.string().nullable().max(255, "Designation is too long"),
  company_name: Yup.string().nullable().max(255, "Company name is too long"),
  bank_name: Yup.string().nullable().max(255, "Bank name is too long"),
  branch_name: Yup.string().nullable().max(255, "Branch name is too long"),
  address: Yup.string().nullable().max(1000, "Address is too long"),
})

const vehicleValidation = Yup.object({
  name: Yup.string().nullable().max(255, "Name is too long"),
  stock_id: Yup.string().nullable().max(255, "Stock ID is too long"),
  full_chassis: Yup.string().nullable().max(255, "Chassis is too long"),
  full_engine: Yup.string().nullable().max(255, "Engine is too long"),
  year_model: Yup.string().nullable().max(255, "Year is too long"),
  color: Yup.string().nullable().max(255, "Color is too long"),
  cc: Yup.string().nullable().max(255, "CC is too long"),
  fuel: Yup.string().nullable().max(255, "Fuel is too long"),
  brta_reg: Yup.string().nullable().max(255, "BRTA reg is too long"),
  vehicle_description: Yup.string().nullable().max(500, "Description is too long"),
})

const notesValidation = Yup.object({
  notes: Yup.string().nullable().max(1000, "Notes are too long"),
})

const PreOrderSectionEditModal = ({
  isOpen,
  toggle,
  preOrder,
  sectionType,
  onSubmit,
  isSubmitting = false,
}) => {
  const isCustomer = sectionType === "customer"
  const isNotes = sectionType === "notes"

  const initialValues = useMemo(
    () =>
      isCustomer
        ? getCustomerInitialValues(preOrder)
        : isNotes
          ? getNotesInitialValues(preOrder)
          : getVehicleInitialValues(preOrder),
    [preOrder, sectionType, isOpen]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: isCustomer
      ? customerValidation
      : isNotes
        ? notesValidation
        : vehicleValidation,
    onSubmit: async values => {
      if (isCustomer) {
        const customerInfo = {
          name: values.name?.trim() || "",
          mobile: values.mobile?.trim() || "",
          email: values.email?.trim() || "",
          customer_type: values.customer_type,
          designation: values.designation?.trim() || "",
          company_name: values.company_name?.trim() || "",
          bank_name: values.bank_name?.trim() || "",
          branch_name: values.branch_name?.trim() || "",
          address: values.address?.trim() || "",
        }
        await onSubmit({
          pdf_content: {
            ...(preOrder?.pdf_content ?? {}),
            customer_info: customerInfo,
          },
        })
        return
      }

      if (isNotes) {
        await onSubmit({
          notes: values.notes?.trim() || null,
        })
        return
      }

      const vehicleInfo = {
          name: values.name?.trim() || "",
          stock_id: values.stock_id?.trim() || "",
          full_chassis: values.full_chassis?.trim() || "",
          full_engine: values.full_engine?.trim() || "",
          year_model: values.year_model?.trim() || "",
          color: values.color?.trim() || "",
          cc: values.cc?.trim() || "",
          fuel: values.fuel?.trim() || "",
          brta_reg: values.brta_reg?.trim() || "",
        }
        const payload = {
          pdf_content: {
            ...(preOrder?.pdf_content ?? {}),
            vehicle_info: vehicleInfo,
          },
          vehicle_description: values.vehicle_description?.trim() || null,
        }

        const prevChassis = preOrder?.pdf_content?.vehicle_info?.full_chassis ?? ""
        const prevEngine = preOrder?.pdf_content?.vehicle_info?.full_engine ?? ""
        if (
          vehicleInfo.full_chassis !== prevChassis ||
          vehicleInfo.full_engine !== prevEngine
        ) {
          payload.new_vehicle = {
            ...(vehicleInfo.full_chassis
              ? { full_chassis: vehicleInfo.full_chassis }
              : {}),
            ...(vehicleInfo.full_engine
              ? { full_engine: vehicleInfo.full_engine }
              : {}),
            ...(vehicleInfo.year_model
              ? { year_model: vehicleInfo.year_model }
              : {}),
            ...(vehicleInfo.color ? { color: vehicleInfo.color } : {}),
            ...(vehicleInfo.cc ? { cc: vehicleInfo.cc } : {}),
            ...(vehicleInfo.fuel ? { fuel: vehicleInfo.fuel } : {}),
          }
        }

        await onSubmit(payload)
    },
  })

  // reset form on close
  useEffect(() => {
    if (!isOpen) formik.resetForm()
  }, [isOpen])

  const title = isCustomer
    ? "Edit Customer Information"
    : isNotes
      ? "Edit Notes"
      : "Edit Vehicle Information"

  const customerTypeKey = resolveCustomerTypeKey(formik.values.customer_type)
  const meta = getCustomerTypeFieldMeta(customerTypeKey)
  const showOrganization = customerTypeKey === "service" || customerTypeKey === "banker"
  const showBranch = customerTypeKey === "banker"

  return (
    <ThemeModal
      isOpen={isOpen}
      toggle={toggle}
      title={title}
      size="lg"
      modalBodyClass="bg-body-tertiary"
      footer={
        <div className="d-flex justify-content-end gap-2 w-100">
          <Button color="light" onClick={toggle} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={formik.submitForm}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      }
    >
      <Form onSubmit={formik.handleSubmit}>
        {isCustomer ? (
          <Row className="g-3">
            <Col md="6">
              <Label className="form-label">Name</Label>
              <Input
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.name && formik.errors.name)}
              />
              <FormFeedback>{formik.errors.name}</FormFeedback>
            </Col>
            <Col md="6">
              <Label className="form-label">Customer Type</Label>
              <Input
                type="select"
                name="customer_type"
                value={formik.values.customer_type}
                onChange={e => {
                  const next = e.target.value
                  formik.setFieldValue("customer_type", next)
                  const nextKey = resolveCustomerTypeKey(next)
                  if (nextKey === "general") {
                    formik.setFieldValue("designation", "")
                    formik.setFieldValue("company_name", "")
                    formik.setFieldValue("bank_name", "")
                    formik.setFieldValue("branch_name", "")
                  } else if (nextKey === "service") {
                    formik.setFieldValue("bank_name", "")
                    formik.setFieldValue("branch_name", "")
                  } else if (nextKey === "banker") {
                    formik.setFieldValue("company_name", "")
                  }
                }}
                invalid={!!(formik.touched.customer_type && formik.errors.customer_type)}
              >
                {CUSTOMER_TYPES.map(type => (
                  <option key={type} value={type}>
                    {formatCustomerTypeLabel(type)}
                  </option>
                ))}
              </Input>
              <FormFeedback>{formik.errors.customer_type}</FormFeedback>
            </Col>
            <Col md="6">
              <Label className="form-label">Mobile</Label>
              <Input
                name="mobile"
                value={formik.values.mobile}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.mobile && formik.errors.mobile)}
              />
              <FormFeedback>{formik.errors.mobile}</FormFeedback>
            </Col>
            <Col md="6">
              <Label className="form-label">Email</Label>
              <Input
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.email && formik.errors.email)}
              />
              <FormFeedback>{formik.errors.email}</FormFeedback>
            </Col>
            {showOrganization && (
              <Col md="6">
                <Label className="form-label">Designation</Label>
                <Input
                  name="designation"
                  value={formik.values.designation}
                  onChange={formik.handleChange}
                  invalid={
                    !!(formik.touched.designation && formik.errors.designation)
                  }
                />
                <FormFeedback>{formik.errors.designation}</FormFeedback>
              </Col>
            )}
            {showOrganization && (
              <Col md="6">
                <Label className="form-label">{meta.organizationLabel}</Label>
                <Input
                  name={meta.organizationField}
                  value={formik.values[meta.organizationField] ?? ""}
                  onChange={formik.handleChange}
                  invalid={Boolean(
                    formik.touched[meta.organizationField] &&
                      formik.errors[meta.organizationField]
                  )}
                />
                <FormFeedback>
                  {formik.errors[meta.organizationField]}
                </FormFeedback>
              </Col>
            )}
            {showBranch && (
              <Col md="6">
                <Label className="form-label">Branch Name</Label>
                <Input
                  name="branch_name"
                  value={formik.values.branch_name}
                  onChange={formik.handleChange}
                  invalid={
                    !!(formik.touched.branch_name && formik.errors.branch_name)
                  }
                />
                <FormFeedback>{formik.errors.branch_name}</FormFeedback>
              </Col>
            )}
            <Col md="12">
              <Label className="form-label">Address</Label>
              <Input
                type="textarea"
                rows={2}
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.address && formik.errors.address)}
              />
              <FormFeedback>{formik.errors.address}</FormFeedback>
            </Col>
          </Row>
        ) : isNotes ? (
          <Row className="g-3">
            <Col md="12">
              <Label className="form-label">Notes</Label>
              <Input
                type="textarea"
                rows={5}
                name="notes"
                value={formik.values.notes}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.notes && formik.errors.notes)}
                placeholder="Additional notes or comments shown on the PDF..."
              />
              <FormFeedback>{formik.errors.notes}</FormFeedback>
            </Col>
          </Row>
        ) : (
          <Row className="g-3">
            <Col md="12">
              <Label className="form-label">Vehicle Name</Label>
              <Input
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.name && formik.errors.name)}
              />
              <FormFeedback>{formik.errors.name}</FormFeedback>
            </Col>
            <Col md="6">
              <Label className="form-label">Full Chassis</Label>
              <Input
                name="full_chassis"
                placeholder="e.g. NZE141-1234567"
                value={formik.values.full_chassis}
                onChange={formik.handleChange}
                invalid={
                  !!(formik.touched.full_chassis && formik.errors.full_chassis)
                }
              />
              <FormFeedback>{formik.errors.full_chassis}</FormFeedback>
            </Col>
            <Col md="6">
              <Label className="form-label">Full Engine</Label>
              <Input
                name="full_engine"
                placeholder="e.g. 1NZ-1234567"
                value={formik.values.full_engine}
                onChange={formik.handleChange}
                invalid={
                  !!(formik.touched.full_engine && formik.errors.full_engine)
                }
              />
              <FormFeedback>{formik.errors.full_engine}</FormFeedback>
            </Col>
            <Col md="4">
              <Label className="form-label">Year/Model</Label>
              <Input
                name="year_model"
                value={formik.values.year_model}
                onChange={formik.handleChange}
                invalid={
                  !!(formik.touched.year_model && formik.errors.year_model)
                }
              />
              <FormFeedback>{formik.errors.year_model}</FormFeedback>
            </Col>
            <Col md="4">
              <Label className="form-label">Color</Label>
              <Input
                name="color"
                value={formik.values.color}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.color && formik.errors.color)}
              />
              <FormFeedback>{formik.errors.color}</FormFeedback>
            </Col>
            <Col md="4">
              <Label className="form-label">CC</Label>
              <Input
                name="cc"
                value={formik.values.cc}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.cc && formik.errors.cc)}
              />
              <FormFeedback>{formik.errors.cc}</FormFeedback>
            </Col>
            <Col md="6">
              <Label className="form-label">Fuel</Label>
              <Input
                name="fuel"
                value={formik.values.fuel}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.fuel && formik.errors.fuel)}
              />
              <FormFeedback>{formik.errors.fuel}</FormFeedback>
            </Col>
            <Col md="12">
              <Label className="form-label">Vehicle Description</Label>
              <Input
                type="textarea"
                rows={2}
                name="vehicle_description"
                value={formik.values.vehicle_description}
                onChange={formik.handleChange}
                invalid={
                  !!(
                    formik.touched.vehicle_description &&
                    formik.errors.vehicle_description
                  )
                }
              />
              <FormFeedback>{formik.errors.vehicle_description}</FormFeedback>
            </Col>
          </Row>
        )}
      </Form>
    </ThemeModal>
  )
}

export default PreOrderSectionEditModal